import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Image as ImageIcon, X } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { decodeQrFromFile, decodeQrFromImageData } from './qrDecode';

const SCAN_INTERVAL_MS = 150;
const SCAN_WIDTH = 960;

export function QrScanner({ onDetected, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const doneRef = useRef(false);
  const [cameraError, setCameraError] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [busy, setBusy] = useState(false);

  const finish = useCallback((text) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDetected(text);
  }, [onDetected]);

  useEffect(() => {
    let timer;
    let cancelled = false;

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError('insecure');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        video.srcObject = stream;
        await video.play();

        const tick = async () => {
          if (cancelled || doneRef.current) return;
          if (video.readyState >= 2 && video.videoWidth) {
            try {
              const canvas = canvasRef.current;
              const scale = Math.min(1, SCAN_WIDTH / video.videoWidth);
              canvas.width = Math.round(video.videoWidth * scale);
              canvas.height = Math.round(video.videoHeight * scale);
              const context = canvas.getContext('2d', { willReadFrequently: true });
              context.drawImage(video, 0, 0, canvas.width, canvas.height);
              const text = await decodeQrFromImageData(context.getImageData(0, 0, canvas.width, canvas.height));
              if (text) return finish(text);
            } catch {
              // A failed frame is not fatal; try the next one.
            }
          }
          timer = setTimeout(tick, SCAN_INTERVAL_MS);
        };
        tick();
      } catch (error) {
        setCameraError(error?.name === 'NotAllowedError' ? 'denied' : 'unavailable');
      }
    };

    start();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, [finish]);

  const handlePhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setPhotoError('');
    try {
      const text = await decodeQrFromFile(file);
      if (text) finish(text);
      else setPhotoError('No se encontró un QR en la foto. Acércate y que el QR se vea completo y nítido.');
    } catch {
      setPhotoError('No se pudo leer la imagen.');
    } finally {
      setBusy(false);
    }
  };

  const cameraMessages = {
    insecure: 'La cámara en vivo necesita HTTPS. Usa "Tomar foto del QR".',
    denied: 'Permiso de cámara denegado. Actívalo en el navegador o usa "Tomar foto del QR".',
    unavailable: 'No se pudo abrir la cámara. Usa "Tomar foto del QR".',
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#0B0E14]" role="dialog" aria-label="Escanear QR">
      <div className="flex items-center justify-between px-4 py-3">
        <p className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6]">Escanea el QR de la factura</p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar escáner"
          className="flex h-11 w-11 items-center justify-center rounded-lg text-[#9CA3AF] hover:bg-[#151B26]"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="relative mx-4 flex-1 overflow-hidden rounded-xl border border-[#242E42] bg-black">
        <video ref={videoRef} playsInline muted className="h-full w-full object-cover" />
        <canvas ref={canvasRef} className="hidden" />
        {!cameraError && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-[62%] max-h-72 aspect-square rounded-2xl border-2 border-[#E2B05E]/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
          </div>
        )}
        {cameraError && (
          <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
            <p className="text-sm text-[#9CA3AF]">{cameraMessages[cameraError]}</p>
          </div>
        )}
      </div>

      <div className="space-y-3 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        {photoError && <p className="text-sm text-red-400">{photoError}</p>}
        <label className="block">
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handlePhoto}
            disabled={busy}
          />
          <span className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-[#E2B05E] font-medium text-[#0B0E14]">
            <Camera weight="bold" className="h-5 w-5" />
            {busy ? 'Leyendo…' : 'Tomar foto del QR'}
          </span>
        </label>
        <label className="block">
          <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} disabled={busy} />
          <span className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-[#242E42] text-[#F3F4F6]">
            <ImageIcon className="h-5 w-5" />
            Elegir imagen de la galería
          </span>
        </label>
        <Button type="button" variant="ghost" className="h-11 w-full text-[#9CA3AF]" onClick={onClose}>
          Escribir el código a mano
        </Button>
      </div>
    </div>
  );
}
